// Street-type and directional abbreviations, expanded to a canonical form so
// a stored address and a Google-formatted one compare equal.
const ADDRESS_SYNONYMS = {
  ave: "avenue", av: "avenue", blvd: "boulevard", cir: "circle",
  ct: "court", dr: "drive", expy: "expressway", hwy: "highway",
  ln: "lane", pkwy: "parkway", pl: "place", rd: "road",
  sq: "square", st: "street", ter: "terrace", trl: "trail",
  n: "north", s: "south", e: "east", w: "west",
  ne: "northeast", nw: "northwest", se: "southeast", sw: "southwest",
};
const COUNTRY_TOKENS = new Set(["us", "usa", "united", "states", "america"]);

/**
 * Canonical form of a street address, for equality matching.
 *
 * Google Places and our stored rows describe the same place differently:
 *
 *   stored : "814 Atlas Ave, Madison, WI 53714, US"
 *   google : "814 Atlas Avenue, Madison, WI, USA"
 *
 * The previous normalizer only lowercased and stripped punctuation, so those
 * two never compared equal — "ave" != "avenue", ZIP present vs absent, "us"
 * != "usa". The exact-address branch of address search could therefore never
 * fire, silently reducing every search to its 50m proximity fallback. A
 * contributor's pin can sit well beyond 50m from where Google geocodes the
 * same address, and those searches reported "no stories" for places the map
 * opens fine when clicked directly.
 *
 * Both examples above canonicalise to "814 atlas avenue madison wi".
 */
export const normalizeAddressForMatch = (s) => {
  if (typeof s !== "string") return "";
  const tokens = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  return tokens
    .filter((token, i) => {
      if (COUNTRY_TOKENS.has(token)) return false;
      // ZIP / ZIP+4. Only ever drop a non-leading numeric run — a leading one
      // is the house number ("53703 N Main St" is a valid address).
      if (i > 0 && /^\d{5}(\d{4})?$/.test(token)) return false;
      return true;
    })
    .map((token) => ADDRESS_SYNONYMS[token] || token)
    .join(" ");
};

export const parsePublicUid = (uid) => {
  if (!uid || typeof uid !== "string") return { name: "", email: "" };
  const parts = uid.split("_");
  if (parts.length < 3) return { name: uid, email: "" };
  const email = parts[parts.length - 1];
  const nameParts = parts.slice(0, -1);
  return { name: nameParts.join(" "), email };
};

export const getUserName = (row) => {
  if (row?.user?.display_name) return row.user.display_name;
  if (row?.user?.email) return row.user.email;
  const parsed = parsePublicUid(row?.uid);
  return parsed.name || "Unknown";
};

export const getUserEmail = (row) => {
  if (row?.user?.email) return row.user.email;
  const parsed = parsePublicUid(row?.uid);
  return parsed.email || "Unknown";
};

/**
 * Compute the hover-tooltip label for a marker cluster.
 *
 * Two regimes:
 *
 *   • In-era (yellow) — the cluster has stories for the slider's
 *     current decade. Show up to 25 chars of the location title.
 *     We prefer `loc.title` (the contribution's "Place Name"/header
 *     field, exposed by selectProjectCont.php per client feedback #2)
 *     so the tooltip reads as a NAME ("Master Lock"); we fall back to
 *     `loc.address` for older payloads / stories with no header value.
 *     Same font weight as a normal UI label.
 *
 *   • Out-of-era (grey) — no stories for the current decade. Show
 *     the nearest decade that DOES have content, prefixed:
 *     "1950s: Riverside Park". Title trimmed to 15 chars. Caller
 *     is expected to render this in a lighter font (the returned
 *     `muted` flag exposes the regime so the markup doesn't need
 *     to redo the in-era check).
 *
 *     Tie-breaking: pick the most recent PREVIOUS decade. If the
 *     cluster only has decades AFTER the current one, fall back to
 *     the soonest NEXT decade.
 *
 *   Returns null when there's nothing useful to show (no address,
 *   no years), so callers can `if (label)` and skip rendering a
 *   tooltip altogether.
 *
 *  Shape of the returned object:
 *   {
 *     text:   string  // the user-facing label text
 *     decade: number|null // the decade encoded in the label (grey only)
 *     muted:  boolean // true for the grey regime; caller dims the font
 *   }
 *
 * @param {Object} opts
 * @param {Object} opts.loc           Location row from selectProjectCont.php
 * @param {number} opts.year          Current slider year (any year in decade)
 * @param {boolean} opts.isInTimeline Whether loc.years.includes(year)
 */
/**
 * The name this location carried in a specific decade.
 *
 * A place is often renamed across eras — 107 King St, Madison is
 * "The King Street Arcade" in the 1930s and "Ancora Coffee" in the 2020s.
 * selectProjectCont.php emits `titles_by_year` ONLY for locations whose
 * decades disagree (12% of rows; emitting it for all of them grew the
 * response ~11% with redundant data), so the flat `title` remains the
 * fallback for everything else.
 */
const titleForDecade = (loc, decade) => {
  const byYear = loc.titles_by_year;
  if (byYear && decade != null) {
    const forDecade = byYear[String(decade)];
    if (typeof forDecade === "string" && forDecade.trim()) {
      return forDecade.trim();
    }
  }
  return typeof loc.title === "string" ? loc.title.trim() : "";
};

/**
 * The decade an out-of-era marker is "speaking for" under the current filter.
 *
 * Client request Aug 4 2026: keep ghosting out-of-era sites when a theme
 * filter is on, and paint the outline in that theme's color. A location's
 * `taxonomy_ids` is the UNION across every era, so it can say "this place
 * relates to music" but not "in which decade" — `taxonomy_by_year` (emitted
 * by selectProjectCont.php for locations whose eras disagree) answers that.
 *
 * Tie-breaking matches getMarkerHoverLabel exactly: most recent PREVIOUS
 * decade wins, else the soonest NEXT one. That keeps the square's color, its
 * hover label and the era its popup offers to jump to all describing the SAME
 * decade, instead of three different ones.
 *
 * @param {Object}   opts
 * @param {Object}   opts.loc      Location row from selectProjectCont.php
 * @param {number}   opts.year     Current slider year
 * @param {Function} opts.matches  (taxonomyIds:number[]) => boolean; whether
 *                                 that era's chips satisfy the active filter
 * @returns {{decade:number, taxonomyIds:number[]}|null}
 */
export const nearestMatchingDecade = ({ loc, year, matches }) => {
  if (!loc || typeof matches !== "function") return null;
  const currentDecade = Math.floor(year / 10) * 10;

  // Prefer real per-era chips; fall back to the union so locations without
  // taxonomy_by_year (single-era, or an older payload) still resolve.
  const byYear = loc.taxonomy_by_year;
  const perDecade = new Map();
  if (byYear && typeof byYear === "object") {
    for (const [dec, ids] of Object.entries(byYear)) {
      const d = Math.floor(Number(dec) / 10) * 10;
      if (Number.isFinite(d) && Array.isArray(ids)) {
        perDecade.set(d, ids.map(Number));
      }
    }
  }
  if (perDecade.size === 0) {
    const union = Array.isArray(loc.taxonomy_ids)
      ? loc.taxonomy_ids.map(Number)
      : [];
    const years = Array.isArray(loc.years) ? loc.years : [];
    for (const y of years) {
      const d = Math.floor(Number(y) / 10) * 10;
      if (Number.isFinite(d)) perDecade.set(d, union);
    }
  }
  if (perDecade.size === 0) return null;

  const hits = [...perDecade.entries()]
    .filter(([, ids]) => matches(ids))
    .map(([d]) => d)
    .sort((a, b) => a - b);
  if (hits.length === 0) return null;

  let previous = null;
  for (let i = hits.length - 1; i >= 0; i--) {
    if (hits[i] < currentDecade) {
      previous = hits[i];
      break;
    }
  }
  const next = hits.find((d) => d > currentDecade);
  const target = previous != null ? previous : next != null ? next : null;
  if (target == null) return null;
  return { decade: target, taxonomyIds: perDecade.get(target) || [] };
};

export const getMarkerHoverLabel = ({ loc, year, isInTimeline }) => {
  if (!loc) return null;
  // Prefer the location title (header/"Place Name" field) over the street
  // address so the hover reads as a name (client feedback #2). Fall back to
  // the address when the location carries no title.
  const address = typeof loc.address === "string" ? loc.address.trim() : "";
  const currentDecade = Math.floor(year / 10) * 10;

  if (isInTimeline) {
    // Show the name as of the SELECTED decade. This branch previously ignored
    // `year` entirely and always rendered loc.title, so an in-era marker on
    // the 1930s showed the location's present-day name (client feedback,
    // Aug 3 2026: a 1930s "Marty's Tavern" marker read "Harmony Bar and
    // Grill"). The `year` argument was accepted and silently unused.
    const label = titleForDecade(loc, currentDecade) || address;
    if (!label) return null;
    const text = label.length > 25 ? label.slice(0, 25) + "…" : label;
    return { text, decade: null, muted: false };
  }

  // Grey regime: find the closest decade with content.
  const decades = Array.isArray(loc.years)
    ? Array.from(
        new Set(
          loc.years
            .map((y) => Math.floor(Number(y) / 10) * 10)
            .filter((d) => Number.isFinite(d)),
        ),
      ).sort((a, b) => a - b)
    : [];
  if (decades.length === 0) return null;

  // Most recent decade strictly before "current". Array.find from the
  // END (= reverse scan) gives us the largest <  current.
  let previous = null;
  for (let i = decades.length - 1; i >= 0; i--) {
    if (decades[i] < currentDecade) {
      previous = decades[i];
      break;
    }
  }
  const next = decades.find((d) => d > currentDecade);
  const target = previous != null ? previous : (next != null ? next : null);
  if (target == null) return null;

  // Title/address might still be missing for a grey marker; fall back to a
  // bare decade label so the user at least sees "1950s" on hover.
  // Name as of the decade we're pointing the user AT, not today's name — the
  // label reads "1930s: The King Street Arcade", so the title has to match
  // the decade in the same string.
  const targetLabel = titleForDecade(loc, target) || address;
  const short = targetLabel
    ? (targetLabel.length > 15 ? targetLabel.slice(0, 15) + "…" : targetLabel)
    : "";
  const text = short ? `${target}s: ${short}` : `${target}s`;
  return { text, decade: target, muted: true };
};

// Map marker neutral fills (DESIGN.md §4.5). Unassigned current-era
// markers are solid black; other-era markers are ALWAYS the neutral grey
// (#8a8a8a, matching the polygon/polyline neutrals — NOT the legacy "gray"
// rgb(128) which drifts off the family).
export const MARKER_NEUTRAL_CURRENT = "#000000";
export const MARKER_NEUTRAL_OTHER = "#8a8a8a";

/**
 * Build the Leaflet divIcon config for a square map marker.
 *
 * Era cue (client revision June-18, item 7): the current decade is a SOLID
 * 14px square in the full chip color with NO outline ("solid pink, no black
 * outline"); other eras are a HOLLOW 10px square (transparent fill, 2px
 * border) in NEUTRAL GREY ("we don't know the theme if not yet posted — we
 * can't assume"). Fill-vs-outline + size + the colour-vs-grey split carry the
 * era distinction. The caller passes grey as `color` for other-era markers,
 * so this helper stays generic. `box-sizing:border-box` makes the painted
 * square exactly iconSize so the anchor sits dead-center.
 *
 * Returns a plain config object — { html, className, iconSize, iconAnchor }
 * — rather than calling L.divIcon, so it stays pure and unit-testable
 * without Leaflet. The caller wraps it:
 *   L.divIcon(squareMarkerIcon({ color, isInTimeline })).
 *
 * @param {Object}  opts
 * @param {string}  opts.color         resolved marker color (chip hue current,
 *                                      neutral grey other-era)
 * @param {boolean} opts.isInTimeline  true → current decade (solid+big, no edge),
 *                                      false → other era (hollow grey+small)
 */
export const squareMarkerIcon = ({ color, isInTimeline }) => {
  if (isInTimeline) {
    return {
      html:
        `<div style="width:14px;height:14px;box-sizing:border-box;` +
        `background-color:${color};` +
        `border-radius:2px;"></div>`,
      className: "custom-square-icon",
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    };
  }
  return {
    html:
      `<div style="width:10px;height:10px;box-sizing:border-box;` +
      `background-color:transparent;border:2px solid ${color};` +
      `border-radius:2px;"></div>`,
    className: "custom-square-icon",
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  };
};

// DOC-13 — the client's requested entry-type geometry. Era remains a separate
// fill-vs-outline cue, and taxonomy remains color. A location can aggregate
// both histories and memories, so the third value is a transparent composite
// (square + circle side by side), not a newly invented semantic symbol.
export const ENTRY_KIND = Object.freeze({
  HISTORY: "history",
  MEMORY: "memory",
  MIXED: "mixed",
});

export const getLocationEntryKind = (loc) => {
  const hasHistory = Boolean(loc?.has_history);
  const hasMemory = Boolean(loc?.has_memory);
  if (hasHistory && hasMemory) return ENTRY_KIND.MIXED;
  if (hasMemory) return ENTRY_KIND.MEMORY;
  // Backward-compatible fallback for an older backend payload: every public
  // marker was historically a square, so absence of flags must stay history.
  return ENTRY_KIND.HISTORY;
};

export const getEntryKindLabel = (kind) => {
  if (kind === ENTRY_KIND.MEMORY) return "Memory";
  if (kind === ENTRY_KIND.MIXED) return "History and memory";
  return "History";
};

// Regions/lines cannot become circles without changing their geography.
// A dashed stroke is the equivalent non-color memory cue; histories remain
// solid. Point markers continue to use the client's square/circle solution.
export const entryKindDashArray = (kind) =>
  kind === ENTRY_KIND.MEMORY ? "6 5" : undefined;

const entryGlyphHtml = ({ color, isInTimeline, round }) => {
  const size = isInTimeline ? 14 : 10;
  const paint = isInTimeline
    ? `background-color:${color};`
    : `background-color:transparent;border:2px solid ${color};`;
  return (
    `<span aria-hidden="true" style="display:block;width:${size}px;` +
    `height:${size}px;box-sizing:border-box;${paint}` +
    `border-radius:${round ? "50%" : "2px"};"></span>`
  );
};

/**
 * Leaflet divIcon config carrying both client meanings without overloading:
 *   square/circle     → history/memory
 *   solid/outline     → selected era/another era
 *   color             → active taxonomy
 *
 * Mixed locations keep one map anchor and one popup, but visibly render both
 * symbols side by side. This avoids choosing one story type and hiding the
 * other when the All lens is active.
 */
export const entryTypeMarkerIcon = ({ color, isInTimeline, entryKind }) => {
  const kind = Object.values(ENTRY_KIND).includes(entryKind)
    ? entryKind
    : ENTRY_KIND.HISTORY;
  if (kind === ENTRY_KIND.HISTORY) {
    return squareMarkerIcon({ color, isInTimeline });
  }

  const size = isInTimeline ? 14 : 10;
  if (kind === ENTRY_KIND.MEMORY) {
    return {
      html: entryGlyphHtml({ color, isInTimeline, round: true }),
      className: "custom-entry-icon custom-entry-icon--memory",
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    };
  }

  const gap = 3;
  const width = size * 2 + gap;
  return {
    html:
      `<span aria-hidden="true" style="display:flex;align-items:center;` +
      `gap:${gap}px;width:${width}px;height:${size}px;">` +
      entryGlyphHtml({ color, isInTimeline, round: false }) +
      entryGlyphHtml({ color, isInTimeline, round: true }) +
      `</span>`,
    className: "custom-entry-icon custom-entry-icon--mixed",
    iconSize: [width, size],
    iconAnchor: [width / 2, size / 2],
  };
};